"use client";

import React from "react";
import HeaderContext from "./context";
import type { HeaderProviderProps } from "./types";

const DEFAULT_DELAY = 1000;

function HeaderProvider({ children, ...props }: HeaderProviderProps) {
  const [isVisible, setIsVisible] = React.useState(true);
  const headerRef = React.useRef<HTMLDivElement>(null);
  const prevScrollY = React.useRef(0);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const toggleIsVisible = React.useCallback(() => {
    setIsVisible(value => !value);
  }, []);

  const handleOnScroll = React.useCallback(() => {
    const currentScrollY = window.scrollY;
    const isScrollingDown = currentScrollY > prevScrollY.current;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (isScrollingDown) {
      setIsVisible(false);
    } else {
      setIsVisible(true);
    }

    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, DEFAULT_DELAY);

    prevScrollY.current = currentScrollY;
  }, []);

  React.useEffect(() => {
    window.addEventListener("scroll", handleOnScroll);

    return () => {
      window.removeEventListener("scroll", handleOnScroll);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  });

  const context = React.useMemo(
    () => ({ headerRef, isVisible, setIsVisible, toggleIsVisible }),
    [isVisible, toggleIsVisible],
  );

  return (
    <HeaderContext.Provider value={context} {...props}>
      {children}
    </HeaderContext.Provider>
  );
}

export default HeaderProvider;
